import { useCallback, useEffect, useRef, useState } from 'react'
import { Copy, Check, ExternalLink, Loader2, MessageCircle, RefreshCw, CreditCard, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { dinero } from './formato'

const CADA_MS = 10000
const HASTA_MS = 30 * 60 * 1000

/**
 * Link de pago de una ampliación, A LA VISTA mientras se espera el pago.
 *
 * Antes el link solo vivía en un toast («copiado al portapapeles») que se iba a los 10 s: el
 * asesor no sabía que lo tenía copiado, no podía volver a verlo, y para saber si el cliente ya
 * había pagado tenía que recargar la página. Aquí el link se queda escrito con su total, se
 * copia/abre/comparte con un clic, y la ficha consulta sola a la pasarela cada 10 s
 * (`verificar-pago`): cuando el pago entra, la addenda se aplica y la ficha se refresca.
 */
/**
 * `verificar` (opcional) consulta el pago y devuelve `{ aplicada?, pagada?, reason? }`. Por defecto
 * es la verificación de una ADDENDA; la cotización nueva pasa la suya (`estado-pago`).
 */
export default function LinkDePago({ addendaId, link, moneda, onPagada, verificar, textoPagado, textoMensaje }) {
  const [copiado, setCopiado] = useState(false)
  const [revisando, setRevisando] = useState(false)
  const [aviso, setAviso] = useState('')
  const [agotado, setAgotado] = useState(false)
  const desde = useRef(Date.now())
  const terminado = useRef(false)

  const revisar = useCallback(async ({ manual = false } = {}) => {
    if (terminado.current) return
    setRevisando(true)
    try {
      const r = verificar
        ? (await verificar()) || {}
        : (await carteraService.verificarPagoAddenda(addendaId))?.data || {}
      if (r.aplicada || r.pagada) {
        terminado.current = true
        toast.success(textoPagado || '¡Pago recibido! El paquete nuevo ya está aplicado.', { duration: 8000 })
        onPagada?.()
        return
      }
      if (r.reason === 'aplicacion_fallida') {
        setAviso('El cliente ya pagó, pero el cambio no se pudo aplicar todavía. Se reintenta solo; si persiste, avisa a soporte.')
      } else if (manual) {
        toast('Todavía no hay pago registrado.', { icon: '⏳' })
      }
    } catch {
      if (manual) toast.error('No se pudo consultar el pago ahora.')
    } finally {
      setRevisando(false)
    }
  }, [addendaId, onPagada, verificar, textoPagado])

  useEffect(() => {
    desde.current = Date.now()
    terminado.current = false
    setAgotado(false)
    const t = setInterval(() => {
      if (Date.now() - desde.current > HASTA_MS) {
        clearInterval(t)
        setAgotado(true)
        return
      }
      if (document.visibilityState === 'visible') revisar()
    }, CADA_MS)
    return () => clearInterval(t)
  }, [revisar])

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link.url)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch {
      toast.error('No se pudo copiar: selecciona el link y cópialo a mano.')
    }
  }

  const mensajeWhatsapp = encodeURIComponent(
    `${textoMensaje || 'Hola, este es el link para pagar la ampliación de tu paquete cd-2b'}${link.total != null ? ` (${dinero(link.total, moneda)} con IVA)` : ''}: ${link.url}`,
  )

  return (
    <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-900/20 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-1.5">
          <CreditCard size={15} /> Link de pago
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {link.esPrueba && <Badge variant="warning">Pago de prueba</Badge>}
          <span aria-live="polite" className="text-caption text-neutral-600 dark:text-neutral-400 flex items-center gap-1.5">
            {agotado ? (
              'Dejamos de revisar solos'
            ) : (
              <><Loader2 size={13} className="animate-spin" /> Esperando el pago del cliente</>
            )}
          </span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Link de pago" className="font-mono text-caption" />
        <div className="flex gap-2 shrink-0">
          <Button onClick={copiar} variant={copiado ? 'outline' : 'default'} className="flex-1 sm:flex-none">
            {copiado ? <><Check size={15} className="mr-1.5" /> Copiado</> : <><Copy size={15} className="mr-1.5" /> Copiar</>}
          </Button>
          <Button variant="outline" asChild>
            <a href={link.url} target="_blank" rel="noopener noreferrer" aria-label="Abrir el link de pago">
              <ExternalLink size={15} />
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={`https://wa.me/?text=${mensajeWhatsapp}`} target="_blank" rel="noopener noreferrer" aria-label="Enviar por WhatsApp">
              <MessageCircle size={15} />
            </a>
          </Button>
        </div>
      </div>

      {link.total != null && (
        <p className="text-caption text-neutral-600 dark:text-neutral-400">
          El cliente paga <span className="font-semibold text-neutral-900 dark:text-neutral-50">{dinero(link.total, moneda)}</span>
          {link.neto != null && <> · neto {dinero(link.neto, moneda)} + IVA {dinero(link.iva, moneda)}</>}
          . {verificar ? 'Al pagar, se registra solo y te avisamos.' : 'Al pagar, el paquete nuevo se aplica solo.'}
        </p>
      )}

      {aviso && (
        <p className="text-caption text-warning-700 dark:text-warning-300 flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {aviso}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => revisar({ manual: true })} disabled={revisando}>
          <RefreshCw size={14} className={`mr-1.5 ${revisando ? 'animate-spin' : ''}`} /> Revisar ahora
        </Button>
        <span className="text-micro text-neutral-500 dark:text-neutral-400">
          {agotado ? 'Pulsa «Revisar ahora» cuando el cliente te diga que pagó.' : 'Se revisa solo cada 10 segundos mientras tengas esta pantalla abierta.'}
        </span>
      </div>
    </div>
  )
}
